/**
 * Время турниров приходит с бэка «стенными часами» без смещения
 * (start_time: "2026-09-30 12:00:00"), а зона лежит отдельным полем
 * (timezone: "Europe/Moscow"). new Date() от такой строки берёт зону браузера,
 * поэтому у админа не в Москве и сама дата, и обратный отсчёт уезжали на
 * величину смещения.
 *
 * Здесь строка + зона турнира сворачиваются в абсолютный момент, а наружу
 * он отдаётся уже в локальной зоне администратора.
 *
 * Без moment-timezone: Intl умеет разложить момент по любой зоне, а значит и
 * посчитать её смещение — этого хватает, лишняя зависимость не нужна.
 */

/** Строки с Z или ±HH:MM однозначны сами по себе — зону турнира к ним применять нельзя. */
const HAS_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i
/** "2026-09-30 12:00:00", "2026-09-30T12:00", допускаются доли секунды. */
const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/
/** Бэк отдаёт это вместо null. */
const EMPTY_DATES = ['0000-00-00 00:00:00', '0000-00-00']

const offsetFormatters = new Map()

function offsetFormatter(timeZone) {
    if (!offsetFormatters.has(timeZone)) {
        offsetFormatters.set(timeZone, new Intl.DateTimeFormat('en-US', {
            timeZone,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        }))
    }
    return offsetFormatters.get(timeZone)
}

/** Смещение зоны в миллисекундах на конкретный момент (с учётом перехода на летнее время). */
function zoneOffsetAt(timestamp, timeZone) {
    const parts = offsetFormatter(timeZone).formatToParts(new Date(timestamp))
    const value = {}
    parts.forEach(part => {
        if (part.type !== 'literal') value[part.type] = Number(part.value)
    })

    const asUtc = Date.UTC(value.year, value.month - 1, value.day, value.hour, value.minute, value.second)
    return asUtc - timestamp
}

/**
 * Абсолютный момент из «стенного» времени турнира.
 * @param {string|Date|number} value
 * @param {string} [timeZone] зона турнира; без неё поведение прежнее — зона браузера
 * @returns {Date|null}
 */
export function tournamentDate(value, timeZone) {
    if (value === null || value === undefined || value === '') return null
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value
    if (typeof value === 'number') return new Date(value)

    const raw = String(value).trim()
    if (!raw || EMPTY_DATES.includes(raw)) return null

    const parsedAsIs = new Date(raw.replace(' ', 'T'))
    if (isNaN(parsedAsIs.getTime())) return null

    // смещение уже в строке либо зону не передали — брать нечего
    if (HAS_OFFSET.test(raw) || !timeZone) return parsedAsIs

    const match = WALL_CLOCK.exec(raw)
    if (!match) return parsedAsIs

    const [, year, month, day, hour, minute, second] = match
    const asIfUtc = Date.UTC(+year, +month - 1, +day, +hour, +minute, +(second || 0))

    try {
        // первое приближение берёт смещение не в той точке шкалы, поэтому
        // уточняем ещё раз: на переходе DST смещение до и после отличается
        const firstGuess = asIfUtc - zoneOffsetAt(asIfUtc, timeZone)
        const refined = asIfUtc - zoneOffsetAt(firstGuess, timeZone)
        return new Date(refined)
    } catch (e) {
        // неизвестная зона — лучше показать время как есть, чем сломать экран
        return parsedAsIs
    }
}

/** Дата и время турнира в локальной зоне администратора. */
export function formatTournamentDateTime(value, timeZone, options = {}) {
    const date = tournamentDate(value, timeZone)
    if (!date) return options.empty !== undefined ? options.empty : '—'

    return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        ...(options.format || {}),
    })
}

/** Разница с «сейчас» в миллисекундах: >0 — впереди, <0 — позади. */
export function tournamentTimeDiff(value, timeZone) {
    const date = tournamentDate(value, timeZone)
    return date ? date.getTime() - Date.now() : null
}

/** «через 6 часов» / «3 дня назад» от настоящего момента старта. */
export function formatTournamentRelative(value, timeZone) {
    const diff = tournamentTimeDiff(value, timeZone)
    if (diff === null) return ''

    const abs = Math.abs(diff)
    const minute = 60000, hour = 3600000, day = 86400000

    let unit = 'day'
    let amount = Math.round(diff / day)
    if (abs < hour) {
        unit = 'minute'
        amount = Math.round(diff / minute)
    } else if (abs < day) {
        unit = 'hour'
        amount = Math.round(diff / hour)
    } else if (abs > day * 45) {
        unit = 'month'
        amount = Math.round(diff / (day * 30))
    }

    try {
        return new Intl.RelativeTimeFormat('ru', {numeric: 'auto'}).format(amount, unit)
    } catch (e) {
        return amount >= 0 ? `через ${amount}` : `${Math.abs(amount)} назад`
    }
}

/**
 * Подпись зоны для подсказки: "12:00 (Europe/Moscow)".
 * Нужна там, где админ сверяет введённое организатором время с показанным.
 */
export function formatInTournamentZone(value, timeZone, options = {}) {
    const date = tournamentDate(value, timeZone)
    if (!date || !timeZone) return ''

    try {
        const text = date.toLocaleString('ru-RU', {
            timeZone,
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            ...(options.format || {}),
        })
        return `${text} (${timeZone})`
    } catch (e) {
        return ''
    }
}
